import { useNavigate, useLocation } from "react-router-dom";
import { useGame } from "@/hooks/useGame";
import { useTranslation } from "@/hooks/useTranslation";
import { Button } from "@/components/ui/button.jsx";
import { Badge } from "@/components/ui/badge.jsx";
import { ThemeToggle } from "@/components/ThemeToggle";
import LanguageSelector from "@/components/ui/LanguageSelector";
import { Plus, UserPlus, ArrowLeft, Wifi, WifiOff } from "lucide-react";
import { useColyseus } from "@/hooks/useColyseus.js";
import logoSmall from "../../assets/images/logo_small.webp";

/**
 * Header de navegação para views autenticadas
 */
function AppHeader({ currentPath }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { currentSession, currentUser, activeTables, clearCurrentSession } =
    useGame();
  const { connected } = useColyseus();
  const wsEnabled = true; // Colyseus is always enabled

  const isActive = (path) => currentPath === path;

  // Encontrar a mesa atual do usuário se ele estiver em uma sessão
  const currentTable = currentSession?.tableId
    ? activeTables.find((table) => table.id == currentSession.tableId)
    : null;

  // Verificar se o usuário está participando de uma mesa em andamento
  const userActiveGameTable = activeTables.find(
    (table) =>
      table.status === "playing" &&
      table.players.includes(currentUser?.username)
  );

  // Verificar se estamos em uma página de mesa (lobby ou game)
  const isInTablePage = location.pathname.includes("/table/");

  /**
   * Handle navigation with session management
   */
  const handleNavigate = (path) => {
    if (path === "/tabletop" || path === "/cards") {
      // Clear session when going to general pages
      clearCurrentSession();
    }
    navigate(path);
  };

  return (
    <header
      data-testid="AppHeader-header"
      className="border-b border-[var(--color-border)] bg-[var(--color-card)]"
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1>
              <picture alt={t("navigation.guildForGloryLogo")}>
                <source srcSet={logoSmall} type="image/webp" />
                <img
                  src={logoSmall}
                  alt={t("navigation.guildForGloryLogo")}
                  className="w-10 sm:w-16 h-auto"
                />
              </picture>
            </h1>
            <Badge variant="secondary" className="hidden sm:inline-flex">
              {t("footer.guildForGlory")}
            </Badge>
            {currentSession && currentTable && (
              <Badge variant="outline" className="hidden sm:inline-flex">
                {t("navigation.table")}: {currentSession.tableId}
              </Badge>
            )}
          </div>

          <nav
            data-testid="AppHeader-nav"
            className="flex items-center space-x-2"
          >
            <Button
              variant={isActive("/tabletop") ? "default" : "ghost"}
              onClick={() => handleNavigate("/tabletop")}
              size="sm"
            >
              {t("navigation.lobby")}
            </Button>

            {/* Mostrar "Voltar à Mesa" se o usuário está participando de um jogo ativo */}
            {userActiveGameTable && !isInTablePage && (
              <Button
                variant="default"
                onClick={() =>
                  navigate(`/table/${userActiveGameTable.id}/game`)
                }
                size="sm"
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                {t(
                  "lobby.backToTable",
                  `Back to Table ${userActiveGameTable.id}`
                )}
              </Button>
            )}

            {/* Mostrar botões de Criar/Entrar Mesa apenas se não estiver em página de mesa e não tiver jogo ativo */}
            {!userActiveGameTable &&
              !isInTablePage &&
              isActive("/tabletop") && (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      // Trigger criar mesa dialog do TableTop
                      const createButton = document.querySelector(
                        "[data-create-table-trigger]"
                      );
                      if (createButton) createButton.click();
                    }}
                    size="sm"
                    className="gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    {t("lobby.createTable")}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      // Trigger entrar mesa dialog do TableTop
                      const joinButton = document.querySelector(
                        "[data-join-table-trigger]"
                      );
                      if (joinButton) joinButton.click();
                    }}
                    size="sm"
                    className="gap-2"
                  >
                    <UserPlus className="w-4 h-4" />
                    {t("lobby.joinTable")}
                  </Button>
                </>
              )}

            <Button
              variant={isActive("/cards") ? "default" : "ghost"}
              onClick={() => handleNavigate("/cards")}
              size="sm"
            >
              {t("navigation.cards")}
            </Button>

            <LanguageSelector />
            <ThemeToggle variant="ghost" size="sm" />

            {/* Realtime connection status */}
            <Badge
              role="button"
              title={
                !wsEnabled
                  ? t("connection.disabled")
                  : connected
                  ? t("connection.active")
                  : t("connection.clickToReconnect")
              }
              onClick={() => {
                // Colyseus handles reconnection automatically
              }}
              variant={
                !wsEnabled ? "outline" : connected ? "secondary" : "outline"
              }
              className="ml-2 hidden sm:inline-flex items-center gap-1 cursor-pointer select-none"
            >
              {!wsEnabled ? (
                <WifiOff className="w-3 h-3 text-amber-500" aria-hidden />
              ) : connected ? (
                <Wifi className="w-3 h-3" aria-hidden />
              ) : (
                <WifiOff className="w-3 h-3" aria-hidden />
              )}
              <span className="text-[11px]">
                {!wsEnabled
                  ? t("connection.websocket.offMock")
                  : connected
                  ? t("connection.websocket.connected")
                  : t("connection.websocket.disconnected")}
              </span>
            </Badge>
          </nav>
        </div>
      </div>
    </header>
  );
}

export default AppHeader;
